// Shopping Goal Selection — Conversions section, deterministic check.
// Per-campaign biddable conversion goals, with soft / upper-funnel goals flagged. Campaigns
// bidding to soft goals are sorted first.

import classNames from 'classnames';

import type { EvidenceProps } from './registry';

interface CampaignRow {
  id?: string;
  name: string;
  inheritsAccountDefaults: boolean;
  biddableGoals: string[];
  softGoals: string[];
}

interface GoalSelectionShape {
  totalCampaigns: number;
  withSoftGoalCount: number;
  campaigns: CampaignRow[];
}

export const ShoppingGoalSelectionEvidence = ({ evidence }: EvidenceProps) => {
  const e = evidence as unknown as GoalSelectionShape;

  return (
    <div className='evidence'>
      <section className='evidence__section'>
        <h4 className='evidence__section-title'>
          Goal selection
          <span className='evidence__section-tag evidence__section-tag--context'>
            {e.totalCampaigns} Shopping campaigns
          </span>
        </h4>
        <div className='audience-coverage'>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>{e.totalCampaigns.toLocaleString()}</div>
            <div className='audience-coverage__lbl'>Shopping campaigns</div>
          </div>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>{e.withSoftGoalCount.toLocaleString()}</div>
            <div className='audience-coverage__lbl'>Bidding to soft goals</div>
          </div>
        </div>
      </section>

      <section className='evidence__section'>
        <h4 className='evidence__section-title'>Per-campaign biddable goals</h4>
        <table className='evidence__table evidence__table--pmax-goals'>
          <thead>
            <tr>
              <th>Campaign</th>
              <th>Biddable goals</th>
            </tr>
          </thead>
          <tbody>
            {e.campaigns.map(c => {
              const soft = new Set(c.softGoals);
              return (
                <tr
                  key={c.id ?? c.name}
                  className={classNames({ 'evidence__row--flagged': c.softGoals.length > 0 })}
                >
                  <td>
                    <div>{c.name}</div>
                    {c.inheritsAccountDefaults && (
                      <div className='evidence__action-id'>inherits account-default goals</div>
                    )}
                  </td>
                  <td>
                    <span className='evidence__modifier-list'>
                      {c.biddableGoals.length === 0 ? (
                        <span className='evidence__inline-note'>no biddable goals</span>
                      ) : (
                        c.biddableGoals.map(g => (
                          <span
                            key={g}
                            className={`evidence__audience-chip${soft.has(g) ? ' evidence__brand-chip--exclusion' : ''}`}
                          >
                            {g}
                          </span>
                        ))
                      )}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      <p className='evidence__footnote'>
        These are the conversion-goal categories each Shopping campaign optimizes toward. Tinuiti
        recommends bidding to lower-funnel goals (Purchase for retail) and excluding soft /
        upper-funnel goals (page views, add-to-cart, begin-checkout, view-cart) — a soft goal marked
        biddable dilutes Smart Bidding away from the business outcome. Soft goals are highlighted.
      </p>
    </div>
  );
};
